import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";

const ViewPurchase = () => {
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm();

    const userId = sessionStorage.getItem("userId");
    const purchaseData = async (uid) => {
        const response = await axios.get(`${process.env.REACT_APP_HOST}/api/Purchases/getall/${uid}`);
        return response.data;
    };

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const navigate = useNavigate();

    const fromDate = watch('fromDate');
    const toDate = watch('toDate');

    // GET API START
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ["purchaseData", userId],
        queryFn: () => purchaseData(userId),
        enabled: !!userId,
        refetchOnWindowFocus: false,
    });

    let details = data || [];

    // Filter By search
    const filteredDetails =
        details &&
        details?.filter((item) => {
            const idMatch = String(item?.id).includes(searchTerm);

            // Format salesDate and from/toDate as YYYY-MM-DD
            const itemDateStr = item?.purchaseDate
                ? moment(item?.purchaseDate).format('YYYY-MM-DD')
                : null;

            const fromDateStr = fromDate ? moment(fromDate).format('YYYY-MM-DD') : null;
            const toDateStr = toDate ? moment(toDate).format('YYYY-MM-DD') : null;

            const fromDateMatch = !fromDateStr || itemDateStr >= fromDateStr;
            const toDateMatch = !toDateStr || itemDateStr <= toDateStr;

            return idMatch && fromDateMatch && toDateMatch;
        });

    let totalLength = details && details?.length;

    // DELETE API
    const deleteUser = async (id) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_HOST}/api/Purchases/${userId}/${id}`
            );
            return response.data;
        } catch (error) {
            console.error(`Error deleting user with ID ${id}:`, error);
            throw error;
        }
    };

    const deleteUsers = async (ids) => {
        try {
            const results = await Promise.all(
                ids?.map(async (id) => {
                    return await deleteUser(id);
                })
            );
            return results;
        } catch (error) {
            console.error("Error deleting multiple users:", error.message);
            throw new Error("Failed to delete multiple users");
        }
    };

    // Mutation to delete users
    const { mutate, isPending: deleteLoading } = useMutation({
        mutationFn: deleteUsers,
        onSuccess: () => {
            toast.success("Purchases Deleted Successfully!");
            setSelectedRows([]);
            refetch();
        },
        onError: (error) => {
            console.error("Error deleting rows:", error);
            toast.error("Error deleting rows");
        },
    });

    // Handle checkbox change
    const handleCheckboxChange = (id) => {
        setSelectedRows((prevSelectedRows) => {
            const isSelected = prevSelectedRows?.includes(id);
            if (isSelected) {
                return prevSelectedRows?.filter((rowId) => rowId !== id);
            } else {
                return [...prevSelectedRows, id];
            }
        });
    };
    // Handle delete button click
    const handleDelete = () => {
        if (selectedRows?.length === 0) {
            alert("No rows selected");
            return;
        }
        mutate(selectedRows);
    };

    // DELETE API END



    // Download Excel
    const downloadExcel = () => {
        // Define headers
        const headers = [
            "PURCHASE ID",
            "INVOICE NO",
            "PRODUCT NAME",
            "QUANTITY",
            "UNIT PRICE",
            "GST (%)",
            "TOTAL",
            "STATUS",
            "DATE",
        ];

        // Map data to match the headers order
        const formattedData =
            filteredDetails &&
            filteredDetails?.map((item, index) => {
                const list = item?.vendorDetails || item?.items || (item?.productName || item?.product ? [item] : []);
                return {
                    "PURCHASE ID": item?.id || index + 1,
                    "INVOICE NO": item?.invoiceNo || item?.invoiceNumber || "-",
                    "PRODUCT NAME": list?.map((d) => d?.productName || d?.productNameEnglish || d?.product).join(",") || "",
                    QUANTITY: list?.map((d) => d?.quantity).join(",") || "",
                    "UNIT PRICE": list?.map((d) => d?.purchasePrice || d?.unitPrice).join(",") || "",
                    "GST (%)": list?.map((d) => d?.gst || d?.gstPercentage).join(",") || "",
                    TOTAL: list?.map((d) => {
                        if (d?.totalAmount) return d.totalAmount;
                        const price = parseFloat(d?.purchasePrice || d?.unitPrice) || 0;
                        const qty = parseFloat(d?.quantity) || 0;
                        const gst = parseFloat(d?.gst || d?.gstPercentage) || 0;
                        const other = parseFloat(d?.otherAmount) || 0;
                        const total = (price * qty) + ((price * qty) * gst / 100) + other;
                        return total.toFixed(2);
                    }).join(",") || "",
                    STATUS: item?.status || item?.transactionStatus || "-",
                    DATE: item?.purchaseDate ? moment(item?.purchaseDate).format("DD-MM-YYYY") : "-",
                };
            });

        // Create a new workbook and a worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(formattedData, { header: headers });

        // Append the worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Generate Excel file and trigger download
        XLSX.writeFile(wb, "View Purchase.xlsx");
    };

    return (
        <>
            <div className="view_form_block">
                <div className="view_form_sub_block">
                    {/* DASHBOARD */}
                    <div className="row my-3 d-flex justify-content-center">
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block --first-block">
                            <span className="dashboard-heading">Purchase</span>
                            <span className="dashboard-value">{totalLength || 0}</span>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block">
                            <span className="dashboard-heading">Return</span>
                            <span className="dashboard-value">0</span>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block">
                            <span className="dashboard-heading">Debit</span>
                            <span className="dashboard-value">0</span>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-2 dashborad-card-block --last-block">
                            <span className="dashboard-heading">Total Purchase</span>
                            <span className="dashboard-value">0</span>
                        </div>
                    </div>
                    {/* Filters and Actions Row */}
                    {/* Filter Card */}
                    <div className="card border-0 shadow-sm p-3 mb-4 bg-white">
                        <div className="row g-3 align-items-center">
                            {/* From Date */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    control={control}
                                    name="fromDate"
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            selected={field.value}
                                            onChange={field.onChange}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="From Date"
                                            maxDate={new Date()}
                                            className="filter_date_picker"
                                        />
                                    )}
                                />
                            </div>

                            {/* To Date */}
                            <div className="col-auto w-100-mobile">
                                <Controller
                                    control={control}
                                    name="toDate"
                                    render={({ field }) => (
                                        <DatePicker
                                            {...field}
                                            selected={field.value}
                                            onChange={field.onChange}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="To Date"
                                            minDate={fromDate}
                                            maxDate={new Date()}
                                            className="filter_date_picker"
                                        />
                                    )}
                                />
                            </div>

                            {/* Search */}
                            <div className="col-auto flex-grow-1 w-100-mobile">
                                <input
                                    type="search"
                                    className="view_table_search w-100"
                                    placeholder="Search by Purchase ID"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Actions */}
                            <div className="col-auto d-flex gap-3 w-100-mobile justify-content-center justify-content-md-start flex-wrap">
                                <div
                                    className="download_btn_block_purchase"
                                    onClick={() => navigate("/newPurchase")}
                                >
                                    <button className="download_btn">NEW PURCHASE</button>
                                </div>
                                <div
                                    className="download_btn_block_purchase"
                                    onClick={downloadExcel}
                                >
                                    <div className="download_btn">DOWNLOAD</div>
                                </div>
                                {selectedRows?.length !== 0 && (
                                    <div
                                        className="download_btn_block_purchase"
                                        onClick={() => {
                                            if (window.confirm("Are you sure you want to delete the purchase details?")) {
                                                handleDelete();
                                            }
                                        }}
                                    >
                                        <button className="download_btn">DELETE</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row px-3 my-3">
                        <div className="col-lg-12 view_table_section">
                            <div className="table-responsive view_table_block">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th className="view-table-heading">SELECT</th>
                                            <th className="view-table-heading">PURCHASE ID</th>
                                            <th className="view-table-heading">INVOICE NO</th>
                                            <th className="view-table-heading">PRODUCT NAME</th>
                                            <th className="view-table-heading">QTY</th>
                                            <th className="view-table-heading">UNIT PRICE</th>
                                            <th className="view-table-heading">GST (%)</th>
                                            <th className="view-table-heading">TOTAL</th>
                                            <th className="view-table-heading">STATUS</th>
                                            <th className="view-table-heading">DATE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            isLoading ? (
                                                <>
                                                    <tr>
                                                        <td colSpan={10} className="text-center">
                                                            <h5 className="my-3">L O A D I N G . . .</h5>
                                                        </td>
                                                    </tr>
                                                </>) : (
                                                filteredDetails?.length > 0 ? (
                                                    <>
                                                        {filteredDetails?.map((data, i) => {
                                                            const list = data?.vendorDetails || data?.items || (data?.productName || data?.product ? [data] : []);
                                                            return (
                                                                <tr key={i} onClick={() => navigate("/newPurchase", { state: data })}>
                                                                    <td className="view-table-data" onClick={(e) => e.stopPropagation()}>
                                                                        <input
                                                                            type="checkbox"
                                                                            style={{ accentColor: "blue", width: "18px", height: "18px", cursor: "pointer" }}
                                                                            checked={selectedRows?.includes(data?.purchaseId || data?.id)}
                                                                            onChange={() => handleCheckboxChange(data?.purchaseId || data?.id)}
                                                                        />
                                                                    </td>
                                                                    <td className="view-table-data">{data?.purchaseId || data?.id || "-"}</td>
                                                                    <td className="view-table-data">{data?.invoiceNo || data?.invoiceNumber || "-"}</td>
                                                                    <td className="view-table-data">
                                                                        {list?.length > 0 ? list.map((d) => d?.productName || d?.productNameEnglish || d?.product).join(", ") : "-"}
                                                                    </td>
                                                                    <td className="view-table-data">
                                                                        {list?.length > 0 ? list.map((d) => d?.quantity).join(", ") : "-"}
                                                                    </td>
                                                                    <td className="view-table-data">
                                                                        {list?.length > 0 ? list.map((d) => d?.purchasePrice || d?.unitPrice).join(", ") : "-"}
                                                                    </td>
                                                                    <td className="view-table-data">
                                                                        {list?.length > 0 ? list.map((d) => d?.gst || d?.gstPercentage).join(", ") : "-"}
                                                                    </td>
                                                                    <td className="view-table-data">
                                                                        {list?.length > 0 ? list.map((d) => {
                                                                            if (d?.totalAmount) return d.totalAmount;
                                                                            const price = parseFloat(d?.purchasePrice || d?.unitPrice) || 0;
                                                                            const qty = parseFloat(d?.quantity) || 0;
                                                                            const gst = parseFloat(d?.gst || d?.gstPercentage) || 0;
                                                                            const other = parseFloat(d?.otherAmount) || 0;
                                                                            const total = (price * qty) + ((price * qty) * gst / 100) + other;
                                                                            return total.toFixed(2);
                                                                        }).join(", ") : "-"}
                                                                    </td>
                                                                    <td className="view-table-data">{data?.status || data?.transactionStatus || "-"}</td>
                                                                    <td className="view-table-data">
                                                                        {data?.purchaseDate ? moment(data?.purchaseDate).format("DD-MM-YYYY") : "-"}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center">
                                                            <h5>NO RECORD FOUND</h5>
                                                        </td>
                                                    </tr>
                                                )
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewPurchase;
